import {Button, FormLabel, Image, Input, Stack, Text} from "@chakra-ui/react";
import {getStorage, ref, uploadBytesResumable} from "firebase/storage";
import React, {ChangeEvent, KeyboardEvent, useContext, useEffect, useMemo, useState} from "react";
import {v4 as uuidv4} from "uuid";
import {AppContext, AppContextI} from "../../context/ContextWrapper";
import {Spot} from "../../types";
import RichTextEditor from "../../form/RichTextEditor";
import {firebaseApp} from "../../../firebaseClient";
import {firebaseFunctionsUri} from "../../../graphql/apolloClient";
import {buildSpotCoverImageApiPath, getSpotCoverImagePath} from "../../upload/uploadPaths";
import {AuthContext, AuthContextI} from "../../../auth/FirebaseAuth";

type Props = {
	spot: Spot
}

const normalizeRichText = (value?: string) => {
	const html = (value || "").trim();
	if (!html) {
		return "";
	}

	if (typeof document === "undefined") {
		return html;
	}

	const container = document.createElement("div");
	container.innerHTML = html;
	const textContent = (container.textContent || "").replace(/\u00a0/g, " ").trim();
	if (!textContent) {
		return "";
	}

	return container.innerHTML.trim();
};

const SpotDescriptionDetails = ({spot}: Props) => {
	const appContext: AppContextI = useContext<AppContextI>(AppContext);
	const authContext: AuthContextI = useContext<AuthContextI>(AuthContext);
	const [summary, setSummary] = useState<string>(spot.summary || "");
	const [description, setDescription] = useState<string>(normalizeRichText(spot.description));
	const [isUploadingCoverImage, setIsUploadingCoverImage] = useState<boolean>(false);
	const [fileInputKey, setFileInputKey] = useState<number>(0);
	const [authToken, setAuthToken] = useState<string | undefined>(authContext.getToken());

	useEffect(() => {
		const nextSummary = spot.summary || "";
		const nextDescription = normalizeRichText(spot.description);
		setSummary(nextSummary);
		setDescription(nextDescription);
	}, [spot.id, spot.summary, spot.description]);

	useEffect(() => {
		const existingToken = authContext.getToken();
		if (existingToken) {
			setAuthToken(existingToken);
			return;
		}
		void authContext.getTokenAsync().then((token) => {
			setAuthToken(token);
		});
	}, [authContext]);

	const coverImagePreviewUrl = useMemo(() => {
		const storedValue = (spot.coverImageUrl || "").trim();
		if (!storedValue) {
			return undefined;
		}
		if (/^https?:\/\//i.test(storedValue)) {
			return storedValue;
		}
		const separator = storedValue.includes("?") ? "&" : "?";
		const authorizationQuery = authToken ? `${separator}authorization=${encodeURIComponent(`Bearer ${authToken}`)}` : "";
		return `${firebaseFunctionsUri}${storedValue}${authorizationQuery}`;
	}, [authToken, spot.coverImageUrl]);

	const saveSummary = (nextValue: string) => {
		if (!appContext.isDraft()) {
			return;
		}

		const trimmed = nextValue.trim();
		const currentSummary = (spot.summary || "").trim();
		if (trimmed === currentSummary) {
			setSummary(spot.summary || "");
			return;
		}

		appContext.updateSpot(spot, {
			...spot,
			summary: trimmed || undefined
		});
		setSummary(trimmed);
	};

	const saveDescription = (nextValue: string) => {
		if (!appContext.isDraft()) {
			return;
		}

		const nextDescription = normalizeRichText(nextValue);
		const currentDescription = normalizeRichText(spot.description);
		if (nextDescription === currentDescription) {
			setDescription(currentDescription);
			return;
		}

		appContext.updateSpot(spot, {
			...spot,
			description: nextDescription || undefined
		});
		setDescription(nextDescription);
	};

	const saveCoverImage = async (nextCoverImageUrl?: string) => {
		if (!appContext.isDraft()) {
			return;
		}

		if ((spot.coverImageUrl || undefined) === nextCoverImageUrl) {
			return;
		}

		await appContext.updateSpot(spot, {
			...spot,
			coverImageUrl: nextCoverImageUrl,
		});
	};

	const handleCoverImageSelected = async (event: ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (!file) {
			return;
		}

		if (!appContext.isDraft()) {
			setFileInputKey((current) => current + 1);
			return;
		}

		if (!file.type.startsWith("image/")) {
			appContext.showError("Cover image must be an image file");
			setFileInputKey((current) => current + 1);
			return;
		}

		const areaId = appContext.getArea()?.id || spot.area;
		if (!areaId) {
			appContext.showError("Unable to determine space for cover image upload");
			setFileInputKey((current) => current + 1);
			return;
		}

		setIsUploadingCoverImage(true);
		try {
			const extensionIndex = file.name.lastIndexOf(".");
			const extension = extensionIndex >= 0 ? file.name.substring(extensionIndex) : "";
			const fileName = `${uuidv4()}${extension}`;
			const storagePath = getSpotCoverImagePath(areaId, spot.key);
			const storage = getStorage(firebaseApp);
			const storageRef = ref(storage, `${storagePath}/${fileName}`);
			const uploadTask = uploadBytesResumable(storageRef, file);
			await new Promise<void>((resolve, reject) => {
				uploadTask.on("state_changed", undefined, reject, () => resolve());
			});
			const storedObjectPath = `${storagePath}/${fileName}`;
			await saveCoverImage(buildSpotCoverImageApiPath(storedObjectPath));
		} catch (error) {
			console.error(error);
			appContext.showError("Unable to upload cover image");
		} finally {
			setIsUploadingCoverImage(false);
			setFileInputKey((current) => current + 1);
		}
	};

	const handleRemoveCoverImage = async () => {
		try {
			await saveCoverImage(undefined);
		} catch (error) {
			console.error(error);
			appContext.showError("Unable to remove cover image");
		}
	};

	return <Stack spacing={4}>
		<Stack spacing={1}>
			<FormLabel marginBottom={0}>Summary</FormLabel>
			<Input
				size={"sm"}
				value={summary}
				placeholder={"Short summary"}
				onChange={(event) => setSummary(event.target.value)}
				onBlur={(event) => saveSummary(event.target.value)}
				onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
					if (event.key === "Enter") {
						event.currentTarget.blur();
					}
				}}
				disabled={!appContext.isDraft()}
			/>
		</Stack>

		<Stack spacing={2}>
			<FormLabel marginBottom={0}>Description</FormLabel>
			<RichTextEditor
				value={description}
				onChange={setDescription}
				onBlur={saveDescription}
				placeholder={"Detailed description"}
				isDisabled={!appContext.isDraft()}
			/>
		</Stack>

		<Stack spacing={2}>
			<FormLabel marginBottom={0}>Cover image</FormLabel>
			<Input
				key={fileInputKey}
				size={"sm"}
				type={"file"}
				accept={"image/*"}
				onChange={handleCoverImageSelected}
				disabled={!appContext.isDraft() || isUploadingCoverImage}
				padding={1}
			/>
			{isUploadingCoverImage ? (
				<Text fontSize={"sm"} color={"gray.600"}>Uploading cover image...</Text>
			) : null}
			{spot.coverImageUrl ? (
				<Stack spacing={2}>
					<Image
						src={coverImagePreviewUrl}
						alt={`${spot.name || "Spot"} cover`}
						maxH={"180px"}
						objectFit={"cover"}
						borderRadius={"md"}
						borderWidth={"1px"}
						borderColor={"gray.200"}
						alignSelf={"flex-start"}
					/>
					<Button
						size={"sm"}
						variant={"outline"}
						width={"fit-content"}
						onClick={() => {
							void handleRemoveCoverImage();
						}}
						disabled={!appContext.isDraft() || isUploadingCoverImage}
					>
						Remove cover image
					</Button>
				</Stack>
			) : (
				<Text fontSize={"sm"} color={"gray.600"}>No cover image uploaded.</Text>
			)}
		</Stack>
	</Stack>;
};

export default SpotDescriptionDetails;
